import {
  returnAllTradeDetailsFromGenericParams,
} from "@/lib/agGridColDefs/colDefGlobalHelpers";
import { NotationToDecimalValueType } from "@/lib/interfaces and Enums/TYPENotations";

/**
 * Determines which dealt price to return based on data level.
 * Returns ER level price if available, otherwise falls back to block/leg level.
 *
 * @param data - The row data from AG Grid params
 * @returns The dealt price string
 */
export function getDealtPriceFromData(data: any): string {
  let dealtPriceToReturn = data.feeAdjustedQuote ?? "";

  if (data.legs?.length > 0) {
    // Not to be run for the legs.
    if (data.legs.length === 1 && data.feeAdjustedQuote === "") {
      // This covers the Outright case where the ER level may not have a dealt price populated yet, but the single leg does. Multi-leg structures (like switches) won't hit this fallback since they have more than 1 leg
      dealtPriceToReturn = data.legs[0].feeAdjustedQuote ?? "";
    }
  }

  return dealtPriceToReturn;
}

/**
 * Determines the notation to use based on trade level.
 *
 * @param params - AG Grid cell params
 * @returns The notation string for formatting
 */
export function getNotationFromParams(
  params: any,
): NotationToDecimalValueType | undefined {
  const { isOp, isOutright, isLeg } =
    returnAllTradeDetailsFromGenericParams(params);

  let notation: NotationToDecimalValueType | undefined;

  if (isOp) {
    notation = params.data?.notation;
  } else if (isLeg) {
    notation = params.data?.notation;
  } else if (isOutright) {
    notation = params.data?.legs?.[0]?.notation;
  } else {
    // ER level uses notationER, fallback to notation if not available
    notation = params.data?.notationER ?? params.data?.notation;
  }

  return notation;
}

/**
 * Checks if the dealt price should be hidden based on status.
 * For structures (non-outright, non-leg) with DealtAway status on OP, hide the dealt price.
 *
 * @param params - AG Grid cell params
 * @param status - The execution request status
 * @returns True if dealt price should be hidden
 */
export function shouldHideDealtPrice(params: any, status: string): boolean {
  const { isOp, isOutright, isLeg } =
    returnAllTradeDetailsFromGenericParams(params);

  // 13243 - Hide Dealt Price for structures where RP is dealt away
  if (!isOutright && !isLeg && isOp) {
    return status === "DealtAway";
  }

  return false;
}

